import { Children, ComponentType, isValidElement, ReactNode, useMemo } from 'react';

/**
 * Use this custom hook if you don't want to pass some children to the generic component in props
 *
 * @param {ReactNode} children
 * @param {ComponentType<unknown>[]} components
 * @returns result
 *
 * @example
 * const [dropdownMenu, alert, button] = useGetComponentsFromChildren(children, [DefaultDropdownMenu, Alert, Button]);
 *
 */
export const useGetComponentsFromChildren = <P>(
	children: ReactNode,
	components: ComponentType<P>[]
): (ReactNode | null | undefined)[] => {
	const result = useMemo(() => {
		const result: (ReactNode | null | undefined)[] = [];

		for (const component of components) {
			Children.forEach(children, (child) => {
				if (isValidElement(child)) {
					if (child.type === component && !result.includes(child)) {
						result.push(child);
					}
				}
			});
		}

		return result;
	}, [children, components]);

	return result;
};
